// Generic reference-value helpers — a stored value that carries BOTH what the
// planner sees and a pointer to the record it resolves to.
//
// Pure — no React, no DOM — so it is safe to import from `server/`. Same file
// shape, same value channel and the same plain-string tolerance as
// `shared/address.ts`, deliberately: `generic_entries.value` is one nullable
// text column for every field type in the product (schema.ts:779), so a
// reference is a JSON object-string in that same column or it is nothing.
//
// 🔴 This is the PILOT SHAPE FOR PEOPLE-RECORD-1 (D-202.3). The institution
// specialisation in `shared/institution.ts` is the first user; a person
// reference is expected to be the second. Keep this file free of anything
// institution-specific — that is the whole reason it is separate.
//
// The tolerance rule, which every reader depends on: a plain string is NOT a
// reference. It is a value the planner typed before this type existed, or one
// they typed and never matched, and it must keep working untouched. Nothing in
// this build rewrites a stored value (G8).

export type ReferenceValue = {
  /** What the planner sees and what every identity surface renders. Always present. */
  display: string;
  /** The registry key this value resolved to, absent when the planner's text matched nothing.
   *  An absent `ref` is the NORMAL case for free typing, never an error. */
  ref?: string;
};

/**
 * The discriminating key. A JSON object is a reference only if it carries
 * `display` as a string — which is what keeps a reference and an address
 * distinguishable in one text column (D-202.4).
 */
const REFERENCE_KEY = "display";

/**
 * Parse a stored value into a ReferenceValue, or null when the value is not a
 * reference object (empty, a plain string, any non-object JSON, or a JSON
 * object without a string `display` — an address, for instance).
 *
 * Mirrors `parseAddress`: null means "not this shape", never "malformed".
 */
export function parseReference(value: string | null | undefined): ReferenceValue | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const obj = parsed as Record<string, unknown>;
    if (typeof obj[REFERENCE_KEY] !== "string") return null;
    const ref = typeof obj.ref === "string" && obj.ref ? obj.ref : undefined;
    return ref === undefined
      ? { display: obj[REFERENCE_KEY] as string }
      : { display: obj[REFERENCE_KEY] as string, ref };
  } catch {
    return null;
  }
}

/**
 * The display string for a stored value. A reference object yields its
 * `display`; anything else (a plain string, empty) is returned unchanged.
 *
 * The passthrough is what lets this be applied to a field's value before —
 * and after — its fieldType is flipped, exactly as `formatAddressSummary`
 * tolerates a `text` field awaiting its flip.
 */
export function formatReferenceSummary(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = parseReference(value);
  return parsed ? parsed.display : value;
}

/**
 * Build the string to STORE. A matched value carries its `ref`; a value the
 * planner typed and left unmatched carries `display` alone.
 *
 * 🔴 Free text always wins: `toReferenceValue("Bob's private lending")` is a
 * complete, valid, non-error value. An unmatched reference is never rejected.
 */
export function toReferenceValue(display: string, ref?: string): string {
  const trimmed = display.trim();
  return JSON.stringify(ref ? { display: trimmed, ref } : { display: trimmed });
}

/**
 * The field types whose stored value is a reference object.
 *
 * 🔴 Added s204 (PEOPLE-RECORD-1) because the pilot's three fieldType-keyed read paths — the
 * executor task-binding identity, its detail cells and the full-plan snapshot — each branched
 * `=== "institution"` in a ternary. A second reference type turns each of those into a third
 * arm, and a third type into a fourth; the arms then drift apart one at a time, which is exactly
 * how the four hand-maintained copies of the FieldType union came to exist.
 *
 * One list, asked by every reader. A NEW reference type belongs here and nowhere else.
 */
const REFERENCE_FIELD_TYPES = new Set<string>(["institution", "contact"]);

export function isReferenceFieldType(fieldType: string | null | undefined): boolean {
  return !!fieldType && REFERENCE_FIELD_TYPES.has(fieldType);
}
